// frontend/src/components/auditor/AuditorCasesClients.js
// Cases and clients read-only tables for audit review.

import React, { useState, useEffect, useCallback } from 'react';
import AuditorTable from './AuditorTable';
import { fetchAllPages } from '../../utils/auditorApi';

const SUB_TABS = [
  { id: 'cases', label: 'Cases' },
  { id: 'clients', label: 'Clients' },
];

const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

const StageBadge = ({ stage }) => {
  const colors = {
    'Open': 'bg-green-100 text-green-700',
    'In Progress': 'bg-blue-100 text-blue-700',
    'Closed': 'bg-gray-200 text-gray-600',
    'Archived': 'bg-slate-200 text-slate-600',
    'On Hold': 'bg-amber-100 text-amber-700',
    'Pending': 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[stage] || 'bg-gray-100 text-gray-600'}`}>
      {stage || '—'}
    </span>
  );
};

export default function AuditorCasesClients({ authAxios }) {
  const [subTab, setSubTab] = useState('cases');
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const [c, cl] = await Promise.all([
      fetchAllPages(authAxios, 'cases/'),
      fetchAllPages(authAxios, 'clients/'),
    ]);
    setCases(c);
    setClients(cl);
    if (c.length === 0 && cl.length === 0) {
      setFetchError('No case/client data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Stats
  // [Auditor figures reconciliation] Case.status's real choices are Open/
  // Active/Pending/On_Hold/Closed/Archived (legal_ops/models.py) — there is
  // no `stage`, `is_closed`, or `is_archived` field on CaseSerializer at
  // all (the workflow-stage field is `case_stage`). The `!c.is_closed &&
  // !c.is_archived` fallback therefore evaluated to `true` for every case
  // regardless of real status, so "Open / Active" always equalled the full
  // case count — including cases that were also correctly counted as
  // Closed by the sibling filter, so Open+Closed could exceed Total Cases.
  // Bucketed into the two mutually-exclusive groups so they always sum to
  // the total.
  const openCases = cases.filter(c => ['Open', 'Active', 'Pending', 'On_Hold'].includes(c.status)).length;
  const closedCases = cases.filter(c => c.status === 'Closed' || c.status === 'Archived').length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Cases &amp; Clients</h2>
        <p className="text-sm text-gray-500 mt-1">All firm cases and client records.</p>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Cases', value: cases.length, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Open / Active', value: openCases, color: 'text-green-700 bg-green-50 border-green-200' },
          { label: 'Closed', value: closedCases, color: 'text-gray-700 bg-gray-50 border-gray-200' },
          { label: 'Total Clients', value: clients.length, color: 'text-blue-700 bg-blue-50 border-blue-200' },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl border p-4 ${c.color}`}>
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">{c.label}</p>
            <p className="text-2xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              subTab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'cases' && (
        <AuditorTable
          title={`Cases (${cases.length})`}
          data={cases}
          columns={[
            // [WF-B12] `reference` never existed on CaseSerializer — the
            // real field is `case_number`.
            { key: 'case_number', label: 'Reference' },
            { key: 'title', label: 'Title' },
            { key: 'client_full_name', label: 'Client', render: (v, row) => row.client_full_name || '—' },
            { key: 'case_type', label: 'Type', render: (v, row) => row.case_type_display || row.case_type || '—' },
            { key: 'status', label: 'Status', render: (v) => <StageBadge stage={v} /> },
            // [WF-B12 / compliance-critical] `case_stage` is the field the
            // FIA/KYC gate (WF-D3/G2) actually reads — this column
            // previously referenced a `stage` field that doesn't exist at
            // all, so an Auditor had no way to see, and therefore no way
            // to independently verify, what stage a case was actually in
            // when that gate ran. `phase`/`approval_status` are shown
            // alongside it for the same reason: they were computed
            // server-side (CaseSerializer already exposes all three) but
            // never rendered anywhere in this portal.
            { key: 'case_stage', label: 'Case Stage', render: (v, row) => row.case_stage || '—' },
            { key: 'phase_display', label: 'Phase', render: (v, row) => row.phase_display || row.phase || '—' },
            { key: 'approval_status_display', label: 'Approval', render: (v, row) => row.approval_status_display || row.approval_status || '—' },
            // [WF-B12] `responsible_attorney_name`/`responsible_attorney_details`
            // never existed either — CaseSerializer's equivalent is the
            // primary-partner fields (`partner_first_name`/`partner_last_name`/
            // `partner_username`).
            { key: 'partner_username', label: 'Attorney', render: (v, row) => {
              const name = `${row.partner_first_name || ''} ${row.partner_last_name || ''}`.trim();
              return name || row.partner_username || '—';
            }},
            { key: 'date_opened', label: 'Opened', render: (v) => fmtDate(v) },
            { key: 'date_closed', label: 'Closed', render: (v) => fmtDate(v) },
          ]}
          exportCsv={{ filename: 'cases.csv' }}
          emptyMessage="No cases found."
        />
      )}

      {subTab === 'clients' && (
        <AuditorTable
          title={`Clients (${clients.length})`}
          data={clients}
          columns={[
            // [WF-B12] No `name` field exists on ClientSerializer at all —
            // organizational clients have no first_name/last_name either
            // (only organization_name), so the previous fallback chain left
            // every organizational client's Name column blank.
            { key: 'first_name', label: 'Name', render: (v, row) =>
              row.organization_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—'
            },
            // [WF-B12] Client.CLIENT_TYPE_CHOICES real values are
            // lowercase ('individual'/'organizational') — the uppercase/
            // titlecase comparisons here never matched, so this badge
            // always fell to the gray default (the raw value still showed
            // as text, so this was cosmetic, not a blank cell).
            { key: 'client_type', label: 'Type', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'individual' ? 'bg-blue-100 text-blue-700' :
                v === 'organizational' ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'email', label: 'Email' },
            { key: 'phone_number', label: 'Phone', render: (v, row) => row.phone_number || '—' },
            // [WF-B12] Real fields are `id_or_passport_number` (individual)
            // and `registration_number` (organizational, SerializerMethodField)
            // — `id_number`/`phone` never existed, so this column was blank
            // for every individual client (the registration_number fallback
            // happened to work for organizational ones already).
            { key: 'id_or_passport_number', label: 'ID / Reg #', render: (v, row) => row.id_or_passport_number || row.registration_number || '—' },
            // [WF-B12, compliance-critical] `risk_level` is a real KYC
            // field on ClientSerializer that was never rendered anywhere
            // in this portal at all — not a wrong-name bug, a field that
            // reached the API and was simply dropped before render.
            { key: 'risk_level', label: 'Risk Level', render: (v) => v || '—' },
            { key: 'created_at', label: 'Since', render: (v) => fmtDate(v) },
            // [WF-B12] `is_active` never existed — Client only has
            // `archived` (inverted: true means hidden from active lists).
            // Because `row.is_active` was always undefined, `undefined !==
            // false` is true, so this column showed "Active: Yes" for
            // every client including archived ones — a wrong value, not a
            // blank one.
            { key: 'archived', label: 'Active', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v !== true ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>{v !== true ? 'Yes' : 'No'}</span>
            )},
          ]}
          exportCsv={{ filename: 'clients.csv' }}
          emptyMessage="No clients found."
        />
      )}
    </div>
  );
}
